/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1904279553 — Відновлення сесії після перезавантаження */

import { CT_TYPES, DEVICE_STATUS } from '../../../shared/constants';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { debug, getVmDetails } from '../../../shared/lib/general';
import { getOrderInfo } from '../api/order.api';

interface UseConnectingPollingArgs {
    orderId: string;
    timerTime: number;
    setIsConnected: (v: boolean) => void;
    onError: (error: unknown, page?: string) => void;
}

/**
 * Polls /api/order for the current order while the Connecting screen
 * waits for the apparatus's MQTT ack. Returns a `ping()` function the
 * screen calls on every timer tick; flips `isConnected` when
 * device_status transitions to CONNECTED (or, for the Pulse-family
 * inhibit case, raises an explicit error).
 */
export const useConnectingPolling = ({
    orderId,
    timerTime,
    setIsConnected,
    onError,
}: UseConnectingPollingArgs) => {
    const vm = useAppStore((s) => s.vm);
    const setOrder = useAppStore((s) => s.setOrder);
    const { isCT10 } = getVmDetails(vm);

    const ping = async (): Promise<void> => {
        if (!orderId) {
            debug('Order ID is not set', 'error');
            return;
        }

        debug('Ping order details', 'info');

        try {
            const orderInfo = await getOrderInfo(orderId, timerTime);
            setOrder(orderInfo);

            const isVmOnline = orderInfo.device_status === DEVICE_STATUS.CONNECTED;
            const isVmInhibit =
                orderInfo.device_status === DEVICE_STATUS.COMPLETED &&
                vm.ct_type === CT_TYPES.NULL;
            if (isVmInhibit) {
                onError(
                    'Inhibit error for water and pulse: device_status = 6',
                    'connecting',
                );
                // onError navigates to the error screen. Without this return the
                // fridge branch below would then flip isConnected behind the
                // screen that already replaced this one, and Connecting would
                // navigate a second time on top of the error page.
                return;
            }
            if (isCT10 || isVmOnline) {
                setIsConnected(true);
            }
        } catch (e: unknown) {
            debug('Error loading order details (ping) info', 'error');
            const message = e instanceof Error ? e.message : '';
            onError('Error loading connection info: ' + message, 'connecting');
        }
    };

    return { ping };
};
